import { create } from 'zustand';
import type { User as FirebaseUser } from 'firebase/auth';
import type { AdminMe } from '@staff-app/shared-types';

interface AuthState {
  firebaseUser: FirebaseUser | null;
  token: string | null;
  /** Staff admin identity from `staffMe` query. null when user is signed in but not approved. */
  adminUser: AdminMe | null;
  /** True once the staffMe query has settled (success or 403). */
  adminUserLoaded: boolean;
  loading: boolean;

  setAuth: (data: { firebaseUser: FirebaseUser; token: string }) => void;
  setToken: (token: string) => void;
  setAdminUser: (adminUser: AdminMe | null) => void;
  setAdminUserLoaded: (loaded: boolean) => void;
  setLoading: (loading: boolean) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  firebaseUser: null,
  token: null,
  adminUser: null,
  adminUserLoaded: false,
  loading: true,

  setAuth: ({ firebaseUser, token }) =>
    set((state) => ({
      firebaseUser,
      token,
      // Reset loading only when token actually changes.
      loading: state.token !== token ? true : state.loading,
      // Reset adminUser loaded state on token change so AuthProvider re-runs staffMe.
      adminUserLoaded: state.token !== token ? false : state.adminUserLoaded,
    })),
  setToken: (token) => set({ token }),
  setAdminUser: (adminUser) =>
    set({
      adminUser: adminUser
        ? {
            ...adminUser,
            roles: (adminUser.roles ?? []).map((r) => r.toLowerCase() as AdminMe['roles'][0]),
          }
        : null,
    }),
  setAdminUserLoaded: (adminUserLoaded) => set({ adminUserLoaded }),
  setLoading: (loading) => set({ loading }),
  clearAuth: () =>
    set({
      firebaseUser: null,
      token: null,
      adminUser: null,
      adminUserLoaded: false,
      loading: false,
    }),
}));
